/**
 * ConsequenceRepository - Database operations for disciplineQueue collection
 */

import { BaseRepository, where, limit, orderBy, Timestamp } from './BaseRepository';
import { arrayUnion, collection, query as firestoreQuery, getDocs } from 'firebase/firestore';
import { typeNormalizer } from '../utils/TypeNormalizer';
import { db } from '../../../firebase';
import { recordBehaviourAction } from '../recordBehaviourAction';

export class ConsequenceRepository extends BaseRepository {
  constructor() {
    // CRITICAL: Enable hierarchical paths - disciplineQueue is now stored at schools/{schoolId}/disciplineQueue/{docId}
    super('disciplineQueue', { isHierarchical: true });
  }

  async create(data, options = {}) {
    return data.autoGenerated === true && options.phaseSource
      ? recordBehaviourAction(data, null, options)
      : super.create(data);
  }

  async createWithId(data, id, options = {}) {
    return data.autoGenerated === true && options.phaseSource
      ? recordBehaviourAction(data, id, options)
      : super.createWithId(data, id);
  }

  /**
   * Query hierarchical path only (migration completed)
   */
  async queryBothPaths(constraints = [], schoolId = null) {
    console.log('[ConsequenceRepo] 🔍 queryBothPaths called', { schoolId, constraintsCount: constraints.length });

    if (!schoolId) {
      console.warn('[ConsequenceRepo] ⚠️ schoolId required for hierarchical query');
      return [];
    }

    let allDocs = [];

    try {
      console.log('[ConsequenceRepo] 📂 Querying hierarchical path: schools/' + schoolId + '/disciplineQueue');
      const hierarchicalRef = collection(db, 'schools', schoolId, 'disciplineQueue');
      const hierarchicalQuery = firestoreQuery(hierarchicalRef, ...constraints);
      const hierarchicalSnapshot = await getDocs(hierarchicalQuery);
      console.log('[ConsequenceRepo] ✅ Hierarchical query returned', hierarchicalSnapshot.size, 'docs');
      allDocs.push(...hierarchicalSnapshot.docs);
    } catch (error) {
      console.error('[ConsequenceRepo] ❌ Hierarchical query failed:', error.message);
      throw error;
    }

    return allDocs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Get consequences for a specific student
   * @param {string} studentId - Student ID
   * @param {Object} options - Query options (must include schoolId for hierarchical collections)
   * @returns {Promise<Array>} Array of consequences
   */
  async getByStudentId(studentId, options = {}) {
    const constraints = [
      where('studentId', '==', studentId)
    ];

    if (options.status) {
      constraints.push(where('status', '==', options.status));
    }

    if (options.type) {
      // Try to get typeId first for newer records
      const typeId = await typeNormalizer.normalizeToId(options.type);
      if (typeId) {
        // Query by typeId (preferred for new records)
        constraints.push(where('typeId', '==', typeId));
      } else {
        // Fallback to string type for legacy records
        const normalizedType = await typeNormalizer.normalize(options.type, options.schoolId);
        constraints.push(where('type', '==', normalizedType));
      }
    }

    if (options.undone !== undefined) {
      constraints.push(where('undone', '==', options.undone));
    }

    if (options.startDate) {
      constraints.push(where('issuedAt', '>=', Timestamp.fromDate(options.startDate)));
    }

    if (options.endDate) {
      constraints.push(where('issuedAt', '<=', Timestamp.fromDate(options.endDate)));
    }

    constraints.push(orderBy('issuedAt', 'desc'));

    if (options.limit) {
      constraints.push(limit(options.limit));
    }

    // CRITICAL: Use queryBothPaths to query both hierarchical and flat collections during migration
    return this.queryBothPaths(constraints, options.schoolId);
  }

  /**
   * Get pending consequences requiring action
   * @param {Object} filters - Filter options (must include schoolId for hierarchical collections)
   * @returns {Promise<Array>} Array of pending consequences
   */
  async getPendingConsequences(filters = {}) {
    const constraints = [
      where('status', '==', 'pending'),
      where('undone', '==', false)
    ];

    if (filters.type) {
      // Try to get typeId first for newer records
      const typeId = await typeNormalizer.normalizeToId(filters.type);
      if (typeId) {
        constraints.push(where('typeId', '==', typeId));
      } else {
        const normalizedType = await typeNormalizer.normalize(filters.type, filters.schoolId);
        constraints.push(where('type', '==', normalizedType));
      }
    }

    if (filters.classId) {
      constraints.push(where('classId', '==', filters.classId));
    }

    constraints.push(orderBy('issuedAt', 'desc'));

    // CRITICAL: Use queryBothPaths to query both hierarchical and flat collections during migration
    return this.queryBothPaths(constraints, filters.schoolId);
  }

  /**
   * Get detentions for a specific date
   * @param {Date} date - Date to query
   * @param {Object} filters - Additional filters (must include schoolId for hierarchical collections)
   * @returns {Promise<Array>} Array of detentions
   */
  async getDetentionsByDate(date, filters = {}) {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    // Get typeIds for detention types
    const detentionTypeIds = [];
    const detentionId = await typeNormalizer.normalizeToId('detention');
    const saturdayId = await typeNormalizer.normalizeToId('saturday-detention');

    if (detentionId) detentionTypeIds.push(detentionId);
    if (saturdayId) detentionTypeIds.push(saturdayId);

    const constraints = [];

    // Use typeId if available, otherwise fallback to type strings
    if (detentionTypeIds.length > 0) {
      constraints.push(where('typeId', 'in', detentionTypeIds));
    } else {
      constraints.push(where('type', 'in', ['detention', 'saturday-detention']));
    }

    constraints.push(
      where('scheduledDate', '>=', Timestamp.fromDate(startOfDay)),
      where('scheduledDate', '<=', Timestamp.fromDate(endOfDay)),
      where('undone', '==', false)
    );

    if (filters.served !== undefined) {
      constraints.push(where('served', '==', filters.served));
    }

    constraints.push(orderBy('scheduledDate', 'asc'));

    // CRITICAL: Use queryBothPaths to query both hierarchical and flat collections during migration
    return this.queryBothPaths(constraints, filters.schoolId);
  }

  /**
   * Mark detention as served
   * @param {string} detentionId - Detention ID
   * @param {Object} servedDetails - Details about serving
   * @returns {Promise<Object>} Updated detention
   */
  async markDetentionServed(detentionId, servedDetails = {}) {
    return this.update(detentionId, {
      served: true,
      servedAt: Timestamp.now(),
      servedBy: servedDetails.servedBy || 'system',
      status: 'archived',
      notes: servedDetails.notes || ''
    });
  }

  /**
   * Undo a consequence
   * @param {string} consequenceId - Consequence ID
   * @param {Object} undoDetails - Details about the undo
   * @returns {Promise<Object>} Updated consequence
   */
  async undoConsequence(consequenceId, undoDetails = {}) {
    return this.update(consequenceId, {
      undone: true,
      undoneAt: Timestamp.now(),
      undoneBy: undoDetails.undoneBy || 'system',
      undoneReason: undoDetails.reason || 'Manual undo',
      status: 'undone'
    });
  }

  /**
   * Get demerits for threshold checking
   * @param {string} studentId - Student ID
   * @param {Date} startDate - Start date for period
   * @returns {Promise<Array>} Array of demerits
   */
  async getDemeritsForThreshold(studentId, startDate) {
    const constraints = [
      where('studentId', '==', studentId)
    ];

    // Try to get typeId for demerit
    const demeritTypeId = await typeNormalizer.normalizeToId('demerit');
    if (demeritTypeId) {
      constraints.push(where('typeId', '==', demeritTypeId));
    } else {
      constraints.push(where('type', '==', 'demerit'));
    }

    constraints.push(
      where('undone', '==', false),
      where('issuedAt', '>=', Timestamp.fromDate(startDate))
    );

    constraints.push(orderBy('issuedAt', 'desc'));

    return this.queryDocuments(constraints);
  }

  /**
   * Get total demerit points for a student
   * @param {string} studentId - Student ID
   * @param {Date} startDate - Start date for calculation
   * @returns {Promise<number>} Total demerit points
   */
  async getTotalDemeritPoints(studentId, startDate) {
    const demerits = await this.getDemeritsForThreshold(studentId, startDate);

    return demerits.reduce((total, demerit) => {
      return total + (demerit.points || demerit.consequenceCount || 1);
    }, 0);
  }

  /**
   * Update consequence status
   * @param {string} consequenceId - Consequence ID
   * @param {string} newStatus - New status
   * @param {Object} additionalUpdates - Additional fields to update
   * @returns {Promise<Object>} Updated consequence
   */
  async updateStatus(consequenceId, newStatus, additionalUpdates = {}) {
    return this.update(consequenceId, {
      status: newStatus,
      ...additionalUpdates
    });
  }

  /**
   * Add action to consequence
   * @param {string} consequenceId - Consequence ID
   * @param {Object} actionDetails - Details of the action
   * @returns {Promise<Object>} Updated consequence
   */
  async addAction(consequenceId, actionDetails) {
    const updates = {
      actioned: true,
      actionedAt: Timestamp.now(),
      actionedBy: arrayUnion(actionDetails.actionedBy || 'system')
    };

    if (actionDetails.notes) {
      updates.notes = actionDetails.notes;
    }

    return this.update(consequenceId, updates);
  }

  /**
   * Get recent consequences for statistics
   * @param {number} days - Number of days to look back
   * @param {Object} filters - Optional filters (must include schoolId for hierarchical collections)
   * @returns {Promise<Array>} Array of recent consequences
   */
  async getRecentConsequences(days = 7, filters = {}) {
    console.log('[ConsequenceRepo] 🔍 getRecentConsequences called', { days, filters, hasSchoolId: !!filters.schoolId });
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const constraints = [
      where('issuedAt', '>=', Timestamp.fromDate(startDate)),
      where('undone', '==', false)
    ];

    if (filters.type) {
      // Try to get typeId first for newer records
      const typeId = await typeNormalizer.normalizeToId(filters.type);
      if (typeId) {
        constraints.push(where('typeId', '==', typeId));
      } else {
        const normalizedType = await typeNormalizer.normalize(filters.type, filters.schoolId);
        constraints.push(where('type', '==', normalizedType));
      }
    }

    if (filters.classId) {
      constraints.push(where('classId', '==', filters.classId));
    }

    if (filters.studentId) {
      constraints.push(where('studentId', '==', filters.studentId));
    }

    constraints.push(orderBy('issuedAt', 'desc'));

    // CRITICAL: Use queryBothPaths to query both hierarchical and flat collections during migration
    console.log('[ConsequenceRepo] 📞 Calling queryBothPaths with schoolId:', filters.schoolId);
    const results = await this.queryBothPaths(constraints, filters.schoolId);
    console.log('[ConsequenceRepo] ✅ queryBothPaths returned', results.length, 'consequences');
    return results;
  }

  /**
   * Check for existing accountability consequence
   * @param {string} studentId - Student ID
   * @param {string} thresholdId - Threshold ID
   * @param {Date} startDate - Period start date
   * @returns {Promise<boolean>} True if exists
   */
  async checkExistingAccountabilityConsequence(studentId, thresholdId, startDate) {
    const constraints = [
      where('studentId', '==', studentId),
      where('accountabilityThreshold', '==', thresholdId),
      where('undone', '==', false),
      where('createdAt', '>=', Timestamp.fromDate(startDate))
    ];

    const results = await this.queryDocuments(constraints);
    return results.length > 0;
  }

  /**
   * Get consequences by trigger rule
   * @param {string} ruleId - Rule ID that triggered consequences
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of consequences
   */
  async getByTriggerRule(ruleId, options = {}) {
    const constraints = [
      where('triggeredByRule', '==', ruleId)
    ];

    if (options.studentId) {
      constraints.push(where('studentId', '==', options.studentId));
    }

    if (options.undone !== undefined) {
      constraints.push(where('undone', '==', options.undone));
    }

    constraints.push(orderBy('issuedAt', 'desc'));

    if (options.limit) {
      constraints.push(limit(options.limit));
    }

    return this.queryDocuments(constraints);
  }

  /**
   * Subscribe to consequence changes for a student
   * @param {string} studentId - Student ID
   * @param {Function} callback - Callback for changes
   * @returns {Function} Unsubscribe function
   */
  subscribeToStudentConsequences(studentId, callback) {
    const constraints = [
      where('studentId', '==', studentId),
      where('undone', '==', false),
      orderBy('issuedAt', 'desc')
    ];

    return this.subscribe(callback, constraints);
  }
}
